/* eslint-disable no-debugger */
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  ArrowRightIcon,
  Box,
  GKButton,
  GKTypography,
  Grid2,
  LeftIcon,
  RightIcon,
  Slide,
  Typography,
} from "components";
import { useMutation } from "hooks/useQueries";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { paletteLightTheme } from "utils/ColorTheme";

interface Answer {
  question: number;
  option: number;
  optionPoints: number;
}

const ExternalRiskProfile = () => {
  const { search } = useLocation();

  const [started, setStarted] = useState(false);

  const [currentQuestion, setCurrentQuestion] = useState(0);

  const [checked, setChecked] = useState(true);
  const [answers, setAnswers] = useState<Answer[]>([]);

  const token = new URLSearchParams(search).get("token");

  const advisorId = new URLSearchParams(search).get("advisor_id");

  const clientId = new URLSearchParams(search).get("client_id");

  const portfolioId = new URLSearchParams(search).get("portfolio_id");

  const [isError, setIsError] = useState(false);

  const { data } = useQuery({
    queryKey: ["GET_CLIENT_RISK_PROFILE_QUESTION"],
    queryFn: () =>
      axios.get(
        `${process.env.REACT_APP_BASE_URL}/riskprofile-request/questionaire/?advisor_id=${advisorId}&client_id=${clientId}`,
        {
          headers: {
            Authorization: `Token ${token}`,
            "x-gridkey-user-role": "client",
          },
        },
      ),
    onError: () => {
      setIsError(true);
    },
  });

  // ===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*===*
  const { mutate: riskProfileResponse, isLoading: postLoading } = useMutation({
    mutationKey: ["RISK_PROFILE_QUESTION"],
    mutationFn: (data: any) =>
      axios.post(
        `${process.env.REACT_APP_BASE_URL}/riskprofile-request/${clientId}/questionaire-response/`,
        data,
        {
          headers: {
            Authorization: `Token ${token}`,
            "x-gridkey-user-role": "client",
          },
        },
      ),
    onSuccess: () => {
      setTimeout(() => {
        window.close();
      }, 3000);
    },
  });

  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  const current = data?.data?.data?.riskQuestions[currentQuestion];
  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  const handleNext = (option: any) => {
    const isOptionExist =
      answers?.filter((d) => d?.question === current?.id)?.length !== 0;

    setAnswers((prevAnswers) => {
      return isOptionExist
        ? answers?.map((d) => {
            return {
              ...d,
              option: d?.question === current?.id ? option.id : d?.option,
            };
          })
        : [
            ...prevAnswers,
            {
              question: current?.id,
              option: option.id,
              optionPoints: option.optionPoints,
            },
          ];
    });

    setChecked(false);

    if (currentQuestion + 1 === data?.data?.data?.riskQuestions?.length) {
      setCurrentQuestion((prev) => prev);
      setChecked(true);
    } else {
      setTimeout(() => {
        setChecked(true);
        setCurrentQuestion((prev) => prev + 1);
      }, 300);
    }
  };
  // XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===XXX===
  const riskAnswers = answers[currentQuestion];

  return isError ? (
    <Grid2 container p={1} height={`100vh`} width={`100vw`} overflow={"auto"}>
      <Grid2
        p={1}
        size={12}
        container
        borderRadius={2}
        maxHeight={`calc(100vh - 8px)`}
        maxWidth={`calc(100vw - 8px)`}
        border={`solid 1px ${paletteLightTheme.palette.border.main}`}
        display={"flex"}
        justifyContent={"center"}
        overflow={"auto"}
        alignItems={"center"}
        flexDirection={"column"}
      >
        <img
          src={data?.data?.data?.companyLogo || "LandingPage/newLogo.svg"}
          alt=""
          height={"auto"}
          width={"80px"}
        />
        <Typography>
          You have already submitted the questionnaire. Thank you!
        </Typography>
      </Grid2>
    </Grid2>
  ) : (
    <Grid2
      container
      p={1}
      height={`100vh`}
      width={`100vw`}
      justifyContent={"center"}
      alignItems={"center"}
      overflow={"auto"}
    >
      <Grid2
        p={3}
        size={12}
        container
        borderRadius={2}
        maxHeight={`calc(100vh - 8px)`}
        sx={{
          maxWidth: {
            sm: "50%",
            xs: "100%",
          },
        }}
      >
        {!started && (
          <Grid2
            size={12}
            display={"flex"}
            alignItems={"center"}
            gap={1}
            flexDirection={"column"}
          >
            <img
              src={data?.data?.data?.companyLogo || "Logos/iconLogo.png"}
              alt=""
              height={"auto"}
              width={"100px"}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                marginBottom: "40px",
              }}
            >
              <Typography
                fontWeight={500}
                fontSize={12}
                color="var(--neutral-500)"
              >
                Advisor Name:
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: "var(--grey-700)",
                    marginLeft: 4,
                  }}
                >
                  {data?.data?.data?.advisorName || "advisor name"}
                </span>
              </Typography>

              <Typography
                fontWeight={500}
                fontSize={12}
                color="var(--neutral-500)"
              >
                Client Name:
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: "var(--grey-700)",
                    marginLeft: 4,
                  }}
                >
                  {data?.data?.data?.clientName || "Client name"} {" - "}
                  {data?.data?.data?.clientCode || "0000000"}
                </span>
              </Typography>
            </Box>
            <Grid2
              container
              direction="column"
              alignItems="center"
              gap={2}
              size={12}
            >
              <Grid2>
                <Typography
                  fontSize={24}
                  fontWeight={700}
                  color="var(--black-900)"
                  textAlign="center"
                >
                  Let&apos;s understand your risk appetite!
                </Typography>
              </Grid2>

              <Grid2>
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  color="var(--neutral-500)"
                  textAlign="center"
                  width={"361px"}
                >
                  Tell us a bit about yourself, and we&apos;ll match you with
                  the best investment approach.
                </Typography>
              </Grid2>
            </Grid2>
            <Grid2 container size={12} justifyContent="center" mt={"40px"}>
              <GKButton
                variant="contained"
                endIcon={<ArrowRightIcon />}
                onClick={() => setStarted(true)} // 👈 show the questionnaire
              >
                Get Started
              </GKButton>
            </Grid2>
          </Grid2>
        )}
        {started && (
          <Grid2
            size={12}
            borderTop={`solid 1px ${paletteLightTheme.palette.border.main}`}
            pt={2}
          >
            <Grid2 container spacing={1}>
              <Grid2 size={12}>
                {currentQuestion < data?.data?.data?.riskQuestions.length && (
                  <Slide
                    direction="left"
                    in={checked}
                    mountOnEnter
                    unmountOnExit
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: "column",
                        gap: 16,
                        overflow: "none",
                      }}
                    >
                      <GKTypography
                        fontSize={16}
                        color={"#0D121C"}
                        textAlign={"center"}
                      >
                        {currentQuestion + 1}. {current.questionText}
                      </GKTypography>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          flexDirection: "column",
                          gap: 8,
                          overflow: "none",
                        }}
                      >
                        {current?.options.map((opt: any, index: any) => (
                          <GKButton
                            sx={{
                              width: {
                                xs: "100%",
                                sm: "70%",
                              },
                              p: "12px",
                              margin: "auto",
                            }}
                            key={index}
                            onClick={() => handleNext(opt)}
                            color={
                              riskAnswers?.option === +opt.id
                                ? "primary"
                                : "dark"
                            }
                            variant={
                              riskAnswers?.option === +opt.id
                                ? "contained"
                                : "outlined"
                            }
                          >
                            {opt.option}
                          </GKButton>
                        ))}
                      </div>
                    </div>
                  </Slide>
                )}
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                    marginTop: "24px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    <GKButton
                      variant="outlined"
                      color="dark"
                      onClick={() => setCurrentQuestion((p) => p - 1)}
                      disabled={currentQuestion === 0}
                      sx={{
                        minWidth: "1px",
                      }}
                    >
                      <LeftIcon />
                    </GKButton>
                    <GKButton
                      variant="outlined"
                      color="dark"
                      onClick={() => setCurrentQuestion((p) => p + 1)}
                      disabled={
                        data?.data?.data?.data?.riskprofileQuestionnaire?.data
                          ?.riskQuestions?.length -
                          1 ===
                        currentQuestion
                      }
                      sx={{
                        minWidth: "1px",
                      }}
                    >
                      <RightIcon />
                    </GKButton>
                  </div>
                  <GKButton
                    loading={postLoading}
                    variant="contained"
                    onClick={() => {
                      riskProfileResponse({
                        client: clientId,
                        portfolioId,
                        response: answers,
                      });
                    }}
                  >
                    Confirm & Continue
                  </GKButton>
                </div>
              </Grid2>
            </Grid2>
          </Grid2>
        )}
      </Grid2>
    </Grid2>
  );
};

export default ExternalRiskProfile;
